<style lang="scss" scoped>
.edit-company-scopes {
	&--pending {
		margin-bottom: 32px;
	}
}
</style>

<template lang="pug">
.edit-company-scopes
    el-card(v-if="loading" v-loading="true")
    el-alert.edit-company-scopes--pending(v-if="!loading && hasActiveAudit", title='There is a pending review for this company', description="Your scope changes are currently under review. Please note if you make any subsequent changes before these are approved this will cancel the previous audit and may cause a delay. You can see your current active scopes below." type='warning', effect='dark', :closable="false",show-icon)
    scope-tree(v-if="!loading", ref="scopeTree", :companyId="companyId", :CompanyScopes="CompanyScopes", :optionalStates="optionalStates" :Scopes="Scopes", :activeScopeIds="activeScopeIds", @scope-optional="handleScopeOptional", @submit="handleSubmit")
</template>

<script>
import gql from "graphql-tag";
import ScopesQuery from "@/graphql/queries/Scopes";
import ScopeTree from "@/views/settings/includes/ScopeTree";
import CompanyScopesQuery from "@/graphql/queries/CompanyScopes";
import ConfirmCompanyScopes from "@/components/modals/ConfirmCompanyScopes";
import CompanyScopeAuditsQuery from "@/graphql/queries/CompanyScopeAudits";

export default {
	name: "EditCompanyScopes",
	components: {
		ScopeTree
	},
	props: {
		companyId: {
			type: String,
			required: true
		}
	},
	data: () => ({
		Scopes: [],
		CompanyScopes: [],
		optionalStates: {},
		activeScopeIds: [],
		CompanyScopeAudits: [],
		loading: true,
		selectCompany: false
	}),
	mounted() {
		this.getData();
	},
	computed: {
		hasActiveAudit() {
			if (!this.CompanyScopeAudits) return false;

			return this.CompanyScopeAudits.length && !this.CompanyScopeAudits[0].approved_at;
		}
	},
	methods: {
		handleScopeOptional({ scope, optional }) {
			this.$set(this.optionalStates, scope.id, optional);
		},
		assignNecessity(node, key) {
			return { ...node, optional: this.optionalStates[key] };
		},
		setActiveScopeIds() {
			this.$set(this, "activeScopeIds", this.CompanyScopes.map(s => s.scope_id));
		},
		setOptionalStates() {
			this.Scopes.forEach(scope => {
				const companyScope = this.CompanyScopes.find(css => css.scope_id == scope.id);
				this.$set(this.optionalStates, scope.id, companyScope ? companyScope.optional : false);
			});
		},
		async getData() {
			let response = await this.$apollo.query({
				query: ScopesQuery
			});

			this.Scopes = response.data.Scopes.sort(function(a, b) {
				const [scopeA, scopeB] = [a, b].map(s => s.nice_name.toLowerCase());
				return scopeA < scopeB ? -1 : scopeA > scopeB ? 1 : 0;
			});

			response = await this.$apollo.query({
				query: CompanyScopesQuery,
				fetchPolicy: "no-cache",
				variables: {
					company_id: this.companyId
				}
			});

			this.CompanyScopes = response.data.CompanyScopes;

			this.setActiveScopeIds();
			this.setOptionalStates();

			this.loading = false;
		},
		handleSubmit(checkedNodes) {
			const checked = checkedNodes.map(s => this.assignNecessity(s, s.id));

			const changed = checkedNodes
				.map(cn => this.assignNecessity(cn, cn.id))
				.filter(cn => {
					const companyScope = this.CompanyScopes.find(cs => cs.scope_id == cn.id);
					return companyScope ? cn.optional !== companyScope.optional : false;
				});

			const added = checkedNodes.filter(cn => !this.CompanyScopes.map(cs => cs.scope_id).includes(cn.id)).map(cn => this.assignNecessity(cn, cn.id));

			const removed = this.CompanyScopes.filter(cs => !checkedNodes.map(cn => cn.id).includes(cs.scope_id))
				.map(cs => (cs.nice_name = this.Scopes.find(s => s.id == cs.scope_id)))
				.map(cs => this.assignNecessity(cs, cs.id));

			if (!added.length && !removed.length && !changed.length) {
				return this.$message.error("No changes detected!");
			}

			this.showModal({ added, removed, changed, checked });
		},
		showModal({ added, removed, changed, checked }) {
			this.$vuedals.open({
				title: "Confirm changes",
				component: ConfirmCompanyScopes,
				props: {
					added,
					removed,
					changed
				},
				onClose: async confirm => {
					if (!confirm) return;
					try {
						await this.$apollo.mutate({
							mutation: gql`
								mutation CompanyScopeAudit($company_id: String!, $optional: [ID]!, $required: [ID]!) {
									CompanyScopeAudit(company_id: $company_id, optional: $optional, required: $required) {
										success
									}
								}
							`,
							variables: {
								company_id: this.companyId,
								required: checked.filter(s => !s.optional).map(s => s.id),
								optional: checked.filter(s => s.optional).map(s => s.id)
							}
						});

						this.$message.success("You have successfully submitted your new scopes for review!");
						window.Intercom("trackEvent", "Submit Permissions Audit");
					} catch (error) {
						this.$message.error("Error submitting scopes. Please try again later.");
					}
				}
			});
		}
	},
	apollo: {
		CompanyScopeAudits: {
			query: CompanyScopeAuditsQuery,
			fetchPolicy: "no-cache",
			variables() {
				return {
					company_id: this.companyId
				};
			}
		}
	}
};
</script>
